import React, { memo, useCallback, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import { isEqual } from 'lodash';

import { useAuthenticated } from 'hooks/useAuthenticated';
import { useDebouncedSave } from 'hooks/useDebounceSave';

import { ICalculationsSectionObserverProps } from './types';
import { useHandleCalculation } from '../hooks/useHandleCalculation';
import { IFormContentTypes, IObservedFormsType } from '../utils/form';

export const CalculationsSectionObserver: React.FC<ICalculationsSectionObserverProps> = memo(
  ({ fieldNames, formName }) => {
    const callback = useHandleCalculation(formName);
    const fieldValues = useWatch({ name: fieldNames }) as IFormContentTypes[typeof formName];

    const previousValue = useRef<IFormContentTypes[typeof formName]>(fieldValues);

    const { isAuthenticated, isAuthConfiguring } = useAuthenticated();

    const apiCalculateData = useCallback(
      async (content: IFormContentTypes[IObservedFormsType]) => {
        if (!isEqual(content, previousValue.current) && (isAuthenticated || isAuthConfiguring)) {
          callback(content, previousValue.current);

          previousValue.current = content;
        }
      },
      [fieldNames, isAuthenticated, isAuthConfiguring],
    );

    useDebouncedSave({ data: fieldValues, saveFunction: apiCalculateData, debounceTime: 300 });

    return null;
  },
);
